import { useReactiveVar } from "@apollo/client";
import { useRouter } from "expo-router";
import { type ComponentRef, useCallback, useEffect, useRef } from "react";

import { TextInput } from "@/components/atoms/TextInput";

import SearchbarView from "./SearchbarView";

import { filterValuesVar } from "@/features/player-filter";
import useMobileScreen from "@/hooks/useMobileScreen";
import { useTransfermarktPlayerLookup } from "@/hooks/useTransfermarktPlayerLookup";
import { useTheme } from "@/providers/ThemeProvider";
import { showFilterVar } from "@/stores/filterValues";
import {
  focusSelectedVar,
  isFocusSearchInputVar,
  searchTextVar,
} from "@/stores/isFocusSearchInput";
import {
  defaultTransfermarktLookup,
  transfermarktLookupVar,
} from "@/stores/transfermarktLookup";

/**
 * Props for the SearchbarContainer component
 */
interface Props {
  onFocus?: () => void;
}

/**
 * Container component for search bar with filter integration and text input handling
 * @param root0 - Component props
 * @param root0.onFocus - Callback invoked when search input receives focus
 */
const SearchbarContainer = ({ onFocus }: Props) => {
  const filterValues = useReactiveVar(filterValuesVar);
  const focusValue = useReactiveVar(focusSelectedVar);
  const activeFilterCount = Object.values(filterValues ?? {}).filter(
    v => v !== null && v !== undefined && !(Array.isArray(v) && v.length === 0)
  ).length;
  const inputFieldRef = useRef<ComponentRef<typeof TextInput>>(null!);
  const searchText = useReactiveVar(searchTextVar);
  const router = useRouter();
  const displayDoneButton = !onFocus;
  const isSmallScreen = useMobileScreen();
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";

  const handleDone = useCallback(() => {
    if (isSmallScreen) {
      if (inputFieldRef.current) {
        inputFieldRef.current.blur();
      }
      // Reset search state before navigating so the /search tab doesn't
      // briefly render FilterResults with an empty state flash.
      focusSelectedVar({ type: "default", isShow: false });
      searchTextVar("");
      // Navigate explicitly to /search instead of router.back() to ensure
      // we always return to the search tab, regardless of navigation history.
      // router.back() was unreliable with tab navigation and could navigate
      // to home page instead of search tab (SE-3483)
      router.navigate("/search");
      setTimeout(() => {
        isFocusSearchInputVar(false);
      }, 350);
    } else {
      if (inputFieldRef.current) {
        inputFieldRef.current.blur();
      }
      focusSelectedVar({ type: "default", isShow: false });
      searchTextVar("");
    }
  }, [router, isSmallScreen]);

  const handleFilterDone = useCallback(() => {
    filterValuesVar(null);
    focusSelectedVar({ type: "default", isShow: false });
  }, []);

  const handleChangeInput = useCallback(
    (text: string) => {
      searchTextVar(text);
      if (text.length > 0) {
        focusSelectedVar({ type: "search", isShow: true });
      } else if (!displayDoneButton) {
        focusSelectedVar({ type: "default", isShow: false });
      }
    },
    [displayDoneButton]
  );

  const handleLookupNavigated = useCallback(() => {
    if (inputFieldRef.current) {
      inputFieldRef.current.blur();
    }
    focusSelectedVar({ type: "default", isShow: false });
    searchTextVar("");
  }, []);

  // Pasted Transfermarkt URLs resolve by numeric ID and auto-navigate to the
  // player profile (SE-3599). The lookup state is mirrored into a reactive
  // var so the /search screen (MainContainer) can render the loading and
  // not-found empty states — this searchbar has no results surface of its own.
  const transfermarktLookup = useTransfermarktPlayerLookup(searchText, {
    onNavigated: handleLookupNavigated,
  });

  const handlePressDone = useCallback(() => {
    if (focusValue.type === "search") {
      handleDone();
    } else {
      handleFilterDone();
    }
  }, [focusValue.type, handleDone, handleFilterDone]);

  const handleOpenFilter = useCallback(() => {
    showFilterVar(true);
  }, []);

  useEffect(() => {
    if (displayDoneButton && focusValue.type === "search") {
      const timer = setTimeout(() => {
        if (inputFieldRef.current) {
          inputFieldRef.current.focus();
        }
      }, 350);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [displayDoneButton, focusValue.type]);

  // Mirror the lookup state into the reactive var for MainContainer.
  useEffect(() => {
    transfermarktLookupVar({
      status: transfermarktLookup.isLookingUp
        ? "loading"
        : transfermarktLookup.isNotFound
          ? "notFound"
          : "idle",
      transfermarktId: transfermarktLookup.transfermarktId,
    });
  }, [
    transfermarktLookup.isLookingUp,
    transfermarktLookup.isNotFound,
    transfermarktLookup.transfermarktId,
  ]);

  useEffect(
    () => () => {
      transfermarktLookupVar(defaultTransfermarktLookup);
    },
    []
  );

  return (
    <SearchbarView
      isSmallScreen={isSmallScreen}
      inputFieldRef={inputFieldRef}
      onTextInput={handleChangeInput}
      searchText={searchText}
      onFocus={onFocus}
      onOpenFilter={handleOpenFilter}
      activeFilterCount={activeFilterCount}
      type={focusValue.type}
      onPressDone={handlePressDone}
      isDark={isDark}
    />
  );
};

export default SearchbarContainer;
